import {onMounted, onUnmounted, ref} from "vue";
import {send} from "@/composables/apiClient";

// The "needs attention" counts on the customer menu, from GET menu-counts.
// One poller for the whole app however many components use it; it pauses
// while the tab is hidden and fails quietly (no badges, no snackbar).
export const MENU_COUNTS_EVENT = "refreshMenuCounts";

// Ask the menu to fetch its counts now, after an action that changes one.
export const refreshMenuCounts = () => window.dispatchEvent(new CustomEvent(MENU_COUNTS_EVENT));

const SEVERITY_RANK = {info: 1, warning: 2, error: 3};
const SEVERITY_COLOR = {info: "grey", warning: "warning", error: "error"};

export const menuCountColor = (severity) => SEVERITY_COLOR[severity] ?? "grey";

// Several badges as one: the counts added up, in the worst colour, the sentences joined.
export const combineMenuCounts = (badges) => {
    const list = badges.filter((b) => b?.count > 0);
    if (!list.length) return null;
    return {
        count: list.reduce((sum, b) => sum + b.count, 0),
        severity: list.reduce((worst, b) => ((SEVERITY_RANK[b.severity] ?? 0) > (SEVERITY_RANK[worst] ?? 0) ? b.severity : worst), "info"),
        label: list.map((b) => b.label).join("; "),
    };
};

const POLL_MS = 180000;
const TIMEOUT_MS = 5000;

const counts = ref({});
let users = 0;
let timer = null;
let requestSeq = 0;

const fetchCounts = () => {
    if (document.hidden) return;
    const seq = ++requestSeq;
    send("get", "menu-counts", null, {timeout: TIMEOUT_MS})
        .then((response) => {
            if (seq !== requestSeq) return;
            const data = response?.data?.data;
            counts.value = data && typeof data === "object" && !Array.isArray(data) ? data : {};
        })
        .catch(() => {
            if (seq === requestSeq) counts.value = {};
        });
};

const onVisibility = () => {
    if (!document.hidden) fetchCounts();
};

const start = () => {
    fetchCounts();
    timer = setInterval(fetchCounts, POLL_MS);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener(MENU_COUNTS_EVENT, fetchCounts);
};

const stop = () => {
    clearInterval(timer);
    timer = null;
    requestSeq++;
    counts.value = {};
    document.removeEventListener("visibilitychange", onVisibility);
    window.removeEventListener(MENU_COUNTS_EVENT, fetchCounts);
};

export function useMenuCounts({enabled = true} = {}) {
    onMounted(() => {
        if (!enabled) return;
        if (users++ === 0) start();
    });
    onUnmounted(() => {
        if (!enabled) return;
        if (--users === 0) stop();
    });

    return {counts, refresh: fetchCounts};
}
